import { Flex, Separator } from "@radix-ui/themes";
import { useMemo } from "react";

import {
  ExploreFilterMenu,
  ExploreFiltersValue,
} from "@/v2/features/explore-filter-menu/ExploreFilterMenu";
import { FilterMenu } from "@/v2/components/filter-menu/FilterMenu";
import { FilterMenuType } from "@/v2/components/filter-menu/FilterMenu.model";
import { ExploreInteractionType } from "@/v2/entities/explore/Explore.model";
import { Filter } from "@/v2/entities/explore/Explore.filter";
import { ExploreSearch } from "@/v2/features/explore/ExploreSearch";
import { useSimulator } from "../../../context/SimulatorWrapper";

interface InteractionListHeaderProps {
  search: string;
  onSearchChanged: (search: string) => void;
  activeFilters: ExploreFiltersValue;
  setActiveFilters: (filters: ExploreFiltersValue) => void;
  filterSource?: ExploreInteractionType;
  allowedFilterFields?: string[];
  filter?: Filter;
  label?: string;
  hideFilterCount?: boolean;
  customFilterOptions?: Record<string, Array<{ label: string; value: string }>>;
}

export function InteractionListHeader({
  search,
  onSearchChanged,
  activeFilters,
  setActiveFilters,
  filterSource,
  allowedFilterFields,
  filter,
  label = "Filter",
  hideFilterCount = true,
  customFilterOptions,
}: InteractionListHeaderProps) {
  const { topics } = useSimulator();

  const sessionFilterMenu = useMemo((): FilterMenuType<string> => {
    const menu: FilterMenuType<string> = [];

    if (topics && topics.length > 0) {
      menu.push({
        field: "topic",
        kind: "multi",
        label: "Topic",
        options: topics.map((topic) => ({
          label: topic.topic,
          value: topic.topic,
        })),
      });
    }

    menu.push(
      {
        field: "sessionInteractionType",
        kind: "multi",
        label: "Interaction Type",
        options: [
          { label: "From Conversations", value: "conversations" },
          { label: "From Tickets", value: "tickets" },
          { label: "Pending Review", value: "pending_review" },
        ],
      },
      {
        field: "feedbackType",
        kind: "multi",
        label: "Feedback Type",
        options: [
          { label: "Positive", value: "POSITIVE" },
          { label: "Negative", value: "NEGATIVE" },
          { label: "None", value: "NONE" },
        ],
      }
    );

    if (customFilterOptions?.sessionVersion) {
      menu.push({
        field: "sessionVersion",
        kind: "multi",
        label: "Session Version",
        options: customFilterOptions.sessionVersion,
      });
    }

    if (customFilterOptions?.quackUserId) {
      menu.push({
        field: "quackUserId",
        kind: "multi",
        label: "Quack User ID",
        options: customFilterOptions.quackUserId,
      });
    }

    return menu;
  }, [customFilterOptions, topics]);

  const shouldUseSessionFilters = useMemo(() => {
    const hasSessionFields = allowedFilterFields?.some(
      (field) =>
        field.startsWith("session") ||
        field === "quackUserId" ||
        field === "feedbackType"
    );

    const hasSessionOptions =
      customFilterOptions?.sessionVersion || customFilterOptions?.quackUserId;

    return hasSessionFields || hasSessionOptions;
  }, [allowedFilterFields, customFilterOptions]);

  return (
    <Flex direction="column" width="100%">
      <Flex px="3" py="2" direction="column" justify="between" gap="2">
        <Flex justify="between" gap="2" flexShrink="0" align="center">
          <ExploreSearch search={search} onSearchChanged={onSearchChanged} />
        </Flex>

        {shouldUseSessionFilters ? (
          <FilterMenu
            size="1"
            menu={sessionFilterMenu}
            value={activeFilters}
            onChange={setActiveFilters}
            label={label}
            hideCount={hideFilterCount}
          />
        ) : (
          <ExploreFilterMenu
            size="1"
            source={filterSource}
            value={activeFilters}
            onChange={setActiveFilters}
            label={label}
            filter={filter}
            allowedFields={allowedFilterFields}
            hideCount={hideFilterCount}
          />
        )}
      </Flex>

      <Separator
        orientation="horizontal"
        size="4"
        className="bg-[--q-border]"
      />
    </Flex>
  );
}
